import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';

import { SubTicketListReadState, isSubTicketListSettledFor } from './subTicketListRead';

// The caller's one open session, reduced to what naming it actually needs
export interface RunningTimerSession {
    task_sub_ticket_id: ID;
    task_id: ID;
}

// Structural view of the per-id detail store. Presence of a key at all — loading, loaded or
// errored — means the one fallback read has already been issued for that phase, which is what
// keeps the fallback deduplicated.
export interface RunningTimerDetailStates {
    [subTicketId: string]: { detail: { sub_ticket: { ordinal: number } } | null } | undefined;
}

export interface ResolveRunningTimerViewParams {
    // The Request currently on screen
    taskId: ID;

    activeSession: RunningTimerSession | null;

    // The list read for the Request on screen, and the rows it produced
    listRead: SubTicketListReadState;
    rows: TaskSubTicketResource[] | null;

    detailStates: RunningTimerDetailStates;
}

export interface RunningTimerView {
    // Human label for the phase the timer is on, or null while it is genuinely not known yet
    label: string | null;

    // Parent Request of the running phase, so the callout can link to it
    taskId: ID | null;

    // The single detail read the page should issue, or null when none is warranted
    fetchDetailId: ID | null;
}

const NOTHING_RUNNING: RunningTimerView = { label: null, taskId: null, fetchDetailId: null };

// One owner for the running-timer label and for the decision to fetch it.
//
// The label prefers what is already on screen: when the running phase is a row in this very list
// its ordinal is right there, so naming it costs nothing and warrants no request at all. A detail
// read is only worth issuing when the phase is genuinely unavailable — filtered out, archived, or
// on a different Request.
//
// The order of those two facts is what this function exists to get right. "Not in the list" is
// only meaningful once the list has SETTLED for this Request: before that it means "not known
// yet", and treating it as "absent" fires a detail read for a phase that is about to appear on
// screen anyway. So the decision is deferred until settlement, in either arrival order — active
// session first or list first — and a read that settles failed counts as settled, because it will
// not answer on its own and the fallback is then the only way to name the phase.
export const resolveRunningTimerView = ({
    taskId,
    activeSession,
    listRead,
    rows,
    detailStates,
}: ResolveRunningTimerViewParams): RunningTimerView => {
    if (!activeSession) {
        return NOTHING_RUNNING;
    }

    const runningId = activeSession.task_sub_ticket_id;

    // Rows are only this Request's rows while the read that produced them describes this
    // Request; a previous task's list must never answer for the one on screen
    const currentRows = listRead.taskId === taskId ? rows ?? [] : [];
    const runningRow = currentRows.find((row) => row.id === runningId) ?? null;

    if (runningRow) {
        return { label: `Sub-task #${runningRow.ordinal}`, taskId: activeSession.task_id, fetchDetailId: null };
    }

    const detailState = detailStates[runningId];

    if (detailState?.detail) {
        return {
            label: `Sub-task #${detailState.detail.sub_ticket.ordinal}`,
            taskId: activeSession.task_id,
            fetchDetailId: null,
        };
    }

    // Not on screen and not already fetched. Whether that is worth a request depends entirely on
    // whether the list has actually answered for this Request, and a phase already being read —
    // or already read and failed — is never read a second time.
    const shouldFetch = isSubTicketListSettledFor(listRead, taskId) && detailState === undefined;

    return { label: null, taskId: activeSession.task_id, fetchDetailId: shouldFetch ? runningId : null };
};
